import { useCallback, useEffect, useRef, useState } from "react";
import {
  detectLogLevel,
  extractLogTimestamp,
  formatLogDayPl,
  formatLogLineTextForDisplay,
  LOG_DISPLAY_TIMEZONE,
} from "../lib/logLineParse.js";
import { Select } from "./ui/Select";
import { Switch } from "./ui/controls";
import { LoadingState } from "./ui/states";
import { useToast } from "./ui/Toast";
import { useConfirm } from "./ui/ConfirmDialog";

type Props = {
  setErr: (e: string | null) => void;
};

type LogSource = "app" | "pm2-out" | "pm2-err";

type LogLine = { lineNo: number; text: string };

type Meta = {
  logLevel: string;
  sources: {
    app: { available: boolean; sizeBytes: number; updatedAt: string | null };
    pm2Out: { available: boolean; sizeBytes: number; updatedAt: string | null };
    pm2Err: { available: boolean; sizeBytes: number; updatedAt: string | null };
  };
};

type Chunk = {
  source: LogSource;
  lines: LogLine[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  file: { sizeBytes: number; updatedAt: string | null };
};

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    credentials: "include",
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data as T;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function sourceLabel(source: LogSource): string {
  if (source === "app") return "Log aplikacji";
  if (source === "pm2-out") return "PM2 stdout";
  return "PM2 stderr";
}

type RenderRow =
  | { kind: "day"; label: string; key: string }
  | { kind: "line"; line: LogLine; level: ReturnType<typeof detectLogLevel>; key: string };

function buildRows(lines: LogLine[]): RenderRow[] {
  const rows: RenderRow[] = [];
  let lastDay: string | null = null;
  for (const line of lines) {
    const ts = extractLogTimestamp(line.text);
    const day = ts ? formatLogDayPl(ts) : null;
    if (day && day !== lastDay) {
      rows.push({ kind: "day", label: day, key: `day-${line.lineNo}` });
      lastDay = day;
    }
    rows.push({
      kind: "line",
      line,
      level: detectLogLevel(line.text),
      key: `line-${line.lineNo}`,
    });
  }
  return rows;
}

export function SystemLogsPanel({ setErr }: Props) {
  const toast = useToast();
  const confirm = useConfirm();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [source, setSource] = useState<LogSource>("app");
  const [showPm2, setShowPm2] = useState(false);
  const [level, setLevel] = useState<"" | "debug" | "info" | "warn" | "error">("");
  const [lines, setLines] = useState<LogLine[]>([]);
  const [hasMoreBefore, setHasMoreBefore] = useState(false);
  const [fileMeta, setFileMeta] = useState<Chunk["file"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [clearing, setClearing] = useState(false);

  const viewerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  const loadMeta = useCallback(async () => {
    const m = await adminFetch<Meta>("/admin/api/system-logs/meta");
    setMeta(m);
    return m;
  }, []);

  const loadTail = useCallback(
    async (src: LogSource, lvl: typeof level) => {
      const params = new URLSearchParams();
      params.set("source", src);
      params.set("anchor", "end");
      params.set("limit", "400");
      if (src === "app" && lvl) params.set("level", lvl);

      const chunk = await adminFetch<Chunk>(`/admin/api/system-logs?${params.toString()}`);
      setLines(chunk.lines);
      setHasMoreBefore(chunk.hasMoreBefore);
      setFileMeta(chunk.file);
      return chunk;
    },
    [],
  );

  const loadOlder = useCallback(async () => {
    if (!hasMoreBefore || lines.length === 0 || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const el = viewerRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    const minLine = lines[0]!.lineNo;

    try {
      const params = new URLSearchParams();
      params.set("source", source);
      params.set("anchor", "end");
      params.set("limit", "400");
      params.set("beforeLine", String(minLine));
      if (source === "app" && level) params.set("level", level);

      const chunk = await adminFetch<Chunk>(`/admin/api/system-logs?${params.toString()}`);
      setLines((prev) => [...chunk.lines, ...prev]);
      setHasMoreBefore(chunk.hasMoreBefore);
      setFileMeta(chunk.file);

      requestAnimationFrame(() => {
        if (el) {
          el.scrollTop = el.scrollHeight - prevHeight + prevTop;
        }
      });
    } catch (e) {
      setErr(String((e as Error).message));
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [hasMoreBefore, lines, source, level, setErr]);

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      await loadMeta();
      await loadTail(source, level);
      if (atBottom) {
        requestAnimationFrame(() => {
          const el = viewerRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    } catch (e) {
      setErr(String((e as Error).message));
    }
  }, [atBottom, loadMeta, loadTail, source, level, setErr]);

  const onClearLogs = async () => {
    const label = sourceLabel(source);
    const ok = await confirm({
      title: `Wyczyścić plik „${label}"?`,
      body: "Zawartość pliku logów zostanie usunięta. Tej operacji nie można cofnąć.",
      confirmLabel: "Wyczyść",
      danger: true,
    });
    if (!ok) return;
    setClearing(true);
    try {
      const params = new URLSearchParams();
      params.set("source", source);
      await adminFetch<{ ok: boolean }>(`/admin/api/system-logs?${params.toString()}`, { method: "DELETE" });
      await refresh();
      toast.success("Wyczyszczono logi");
    } catch (e) {
      toast.error("Nie udało się wyczyścić logów", String((e as Error).message));
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadMeta()
      .then(() => loadTail(source, level))
      .catch((e) => setErr(String((e as Error).message)))
      .finally(() => {
        setLoading(false);
        requestAnimationFrame(() => {
          const el = viewerRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        });
      });
  }, [source, level, loadMeta, loadTail, setErr]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      if (atBottom) void refresh();
    }, 5000);
    return () => window.clearInterval(id);
  }, [autoRefresh, atBottom, refresh]);

  const onScroll = () => {
    const el = viewerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAtBottom(nearBottom);
    if (el.scrollTop < 200 && hasMoreBefore && !loadingMoreRef.current) {
      void loadOlder();
    }
  };

  const pm2OutAvailable = meta?.sources.pm2Out.available ?? false;
  const pm2ErrAvailable = meta?.sources.pm2Err.available ?? false;
  const pm2Any = pm2OutAvailable || pm2ErrAvailable;

  const sourceAvailable =
    source === "app"
      ? (meta?.sources.app.available ?? false)
      : source === "pm2-out"
        ? pm2OutAvailable
        : pm2ErrAvailable;

  const lineRange =
    lines.length > 0 ? `Linie ${lines[0]!.lineNo}-${lines[lines.length - 1]!.lineNo}` : "Brak linii";

  const rows = buildRows(lines);

  const sourceOptions = [
    { value: "app", label: "Log aplikacji" },
    ...(pm2OutAvailable ? [{ value: "pm2-out", label: "PM2 stdout" }] : []),
    ...(pm2ErrAvailable ? [{ value: "pm2-err", label: "PM2 stderr" }] : []),
  ];

  if (loading) {
    return (
      <div className="panel">
        <LoadingState text="Ładowanie logów..." />
      </div>
    );
  }

  return (
    <div className="panel system-logs-panel">
      <h2 style={{ marginTop: 0 }}>Logi systemowe</h2>
      <p className="muted">
        Plik aplikacji z pełnymi poziomami logów. Przewiń w górę, aby wczytać starsze wpisy. Podgląd PM2
        jest informacyjny (stdout/stderr procesu). Znaczniki czasu na liście są w strefie{" "}
        {LOG_DISPLAY_TIMEZONE} (pliki na serwerze: UTC).
      </p>

      <div className="row system-logs-toolbar" style={{ flexWrap: "wrap", gap: 12, marginTop: 16 }}>
        <label className="row" style={{ gap: 8, alignItems: "center" }}>
          <span className="muted">Źródło:</span>
          <div style={{ minWidth: 160 }}>
            <Select
              ariaLabel="Źródło logów"
              value={source}
              options={sourceOptions}
              onChange={(v) => {
                setSource(v as LogSource);
                setShowPm2(v !== "app");
              }}
            />
          </div>
        </label>

        {source === "app" ? (
          <label className="row" style={{ gap: 8, alignItems: "center" }}>
            <span className="muted">Poziom:</span>
            <div style={{ minWidth: 130 }}>
              <Select
                ariaLabel="Poziom logów"
                value={level}
                options={[
                  { value: "", label: "Wszystkie" },
                  { value: "debug", label: "debug" },
                  { value: "info", label: "info" },
                  { value: "warn", label: "warn" },
                  { value: "error", label: "error" },
                ]}
                onChange={(v) => setLevel(v as typeof level)}
              />
            </div>
          </label>
        ) : null}

        <button type="button" className="btn-secondary btn--small" onClick={() => void refresh()}>
          Odśwież
        </button>

        <button
          type="button"
          className="btn-secondary btn--small"
          disabled={!sourceAvailable || clearing}
          title={sourceAvailable ? undefined : "Plik logów jest niedostępny"}
          onClick={() => void onClearLogs()}
        >
          {clearing ? "Czyszczenie…" : "Wyczyść logi"}
        </button>

        <label className="row" style={{ gap: 8, alignItems: "center" }}>
          <span id="auto-refresh-label">Auto (5 s)</span>
          <Switch checked={autoRefresh} onChange={setAutoRefresh} ariaLabelledBy="auto-refresh-label" />
        </label>

        <button
          type="button"
          aria-pressed={showPm2}
          className={`btn-secondary btn--small${showPm2 ? " admin-tab--active" : ""}`}
          disabled={!pm2Any}
          title={
            pm2Any
              ? "Przełącz na pliki PM2"
              : "Ustaw PM2_LOG_OUT_PATH i PM2_LOG_ERR_PATH w .env na serwerze"
          }
          onClick={() => {
            if (!pm2Any) return;
            if (!showPm2) {
              setShowPm2(true);
              setSource(pm2OutAvailable ? "pm2-out" : "pm2-err");
            } else {
              setShowPm2(false);
              setSource("app");
            }
          }}
        >
          {showPm2 ? "← Log aplikacji" : "Logi PM2"}
        </button>
      </div>

      {source !== "app" ? (
        <p className="muted" style={{ marginTop: 8 }}>
          Podgląd informacyjny ({sourceLabel(source)}) - to, co trafia na stdout/stderr procesu. Główna
          historia: log aplikacji.
        </p>
      ) : null}

      <div className="system-logs-meta muted" style={{ marginTop: 12, fontSize: "0.85rem" }}>
        <span>{lineRange}</span>
        {fileMeta ? (
          <>
            <span> · </span>
            <span>{formatBytes(fileMeta.sizeBytes)}</span>
            {fileMeta.updatedAt ? (
              <>
                <span> · </span>
                <span>
                  Ostatnia zmiana:{" "}
                  {new Date(fileMeta.updatedAt).toLocaleString("pl-PL", {
                    timeZone: LOG_DISPLAY_TIMEZONE,
                  })}
                </span>
              </>
            ) : null}
          </>
        ) : null}
        {meta ? (
          <>
            <span> · </span>
            <span>LOG_LEVEL={meta.logLevel}</span>
          </>
        ) : null}
      </div>

      <div className="system-logs-viewer-wrap">
        <div
          ref={viewerRef}
          className="system-logs-viewer"
          onScroll={onScroll}
          role="log"
          aria-label="Logi systemowe"
        >
          {loadingMore ? (
            <p className="muted system-logs-loading-more">Wczytywanie starszych…</p>
          ) : null}
          {rows.length === 0 ? (
            <p className="muted">Brak wpisów w tym pliku.</p>
          ) : (
            rows.map((row) =>
              row.kind === "day" ? (
                <div key={row.key} className="system-logs-day">
                  {row.label}
                </div>
              ) : (
                <div
                  key={row.key}
                  className={`system-logs-line${row.level ? ` system-logs-line--${row.level}` : ""}`}
                >
                  <span className="system-logs-line__no">{row.line.lineNo}</span>
                  <span className="system-logs-line__text">
                    {formatLogLineTextForDisplay(row.line.text)}
                  </span>
                </div>
              ),
            )
          )}
        </div>
        {!atBottom ? (
          <button
            type="button"
            className="btn btn-secondary system-logs-jump-bottom"
            onClick={() => {
              const el = viewerRef.current;
              if (el) el.scrollTop = el.scrollHeight;
              setAtBottom(true);
            }}
          >
            Przewiń na dół
          </button>
        ) : null}
      </div>
    </div>
  );
}
